import React, { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useRbac } from "@/lib/rbac";
import { useTheme } from "@/lib/theme";
import { useIsMobile } from "@/lib/mobile.jsx";
import BottomNav from "@/components/BottomNav";
import ReadOnlyBadge from "@/components/ReadOnlyBadge";
import AlertsDrawer, { useAlerts } from "@/components/AlertsDrawer";
import GlobalSearch from "@/components/GlobalSearch";
import FeedbackDialog from "@/components/FeedbackDialog";
import { MySignatureButton } from "@/components/SignatureManager";
import NotificationsBell from "@/components/NotificationsBell";
import { Ldc01NavEntry } from "@/components/ldc01/Ldc01NavEntry";
import StickyHorizontalScrollbar from "@/components/StickyHorizontalScrollbar";
import {
  GaugeIcon, FactoryIcon, ClipboardTextIcon, CalendarCheckIcon, PencilLineIcon,
  WrenchIcon, ShieldCheckIcon, ChartBarIcon, SparkleIcon, DatabaseIcon, SignOutIcon, UserIcon, BellIcon,
  SunIcon, MoonIcon, PackageIcon, CaretDoubleLeftIcon, CaretDoubleRightIcon, LockKeyIcon, ChatCircleTextIcon,
  StorefrontIcon, BuildingsIcon, StackIcon, WarehouseIcon, TruckIcon, CurrencyInrIcon, FileTextIcon,
  CaretDownIcon, CaretRightIcon, ShoppingCartIcon, TreeStructureIcon, WarningIcon,
} from "@phosphor-icons/react";

/**
 * Feb 2026 · Grouped & Collapsible sidebar.
 *
 * Every route is filed under one of five modules. The parent module
 * auto-expands based on the current URL (so navigating into a child
 * page always shows its siblings). Per-user open/closed state is
 * persisted in localStorage. Icons are one-per-module for a clean
 * professional silhouette.
 */
export const TOP_LEVEL = [
  { to: "/", label: "Dashboard", icon: GaugeIcon, exact: true, testId: "nav-dashboard", perm: "main_dashboard:view" },
  { to: "/live", label: "Live Production", icon: FactoryIcon, testId: "nav-live", perm: "live:view" },
  { to: "/sales-dashboard", label: "Sales Dashboard", icon: ChartBarIcon, testId: "nav-sales-dashboard", perm: "sales_dashboard:view" },
  { to: "/dashboards/purchase", label: "Purchase Dashboard", icon: ChartBarIcon, testId: "nav-purchase-dashboard", perm: "dashboards_purchase:view" },
  { to: "/dashboards/inventory", label: "Inventory Dashboard", icon: ChartBarIcon, testId: "nav-inventory-dashboard", perm: "dashboards_inventory:view" },
  { to: "/ai", label: "AI Assistant", icon: SparkleIcon, testId: "nav-ai", perm: "ai:view" },
];

export const GROUPS = [
  {
    key: "sales-dispatch",
    label: "Sales & Dispatch",
    icon: ShoppingCartIcon,
    defaultPerm: "sales_orders:view",
    // Sep 03, 2026 · Standardized grouped structure — two sub-groups
    // (Sales Orders + Sales Dispatch) so this section mirrors the
    // Exports & Dispatch layout for perfect symmetry.
    subGroups: [
      {
        key: "sales-orders",
        label: "Sales Orders",
        items: [
          { to: "/sales-orders",                   label: "Sales Orders",         icon: ClipboardTextIcon, testId: "nav-sales-orders",         perm: "sales_orders:view" },
          { to: "/proforma-invoices",              label: "Proforma Invoices",    icon: FileTextIcon,      testId: "nav-proforma-invoices",    perm: "proforma_invoices:view" },
          { to: "/reports/so-reconciliation",      label: "SO Reconciliation",    icon: ChartBarIcon,      testId: "nav-so-reconciliation",    perm: "reports_so_reconciliation:view" },
          { to: "/reports/domestic-so-reconciliation", label: "SO Reconciliation (Item-wise)", icon: ChartBarIcon, testId: "nav-domestic-so-reconciliation", perm: "reports_so_reconciliation:view" },
          { to: "/reports/pending-sales-order",    label: "Pending Sales Order",  icon: ClipboardTextIcon, testId: "nav-pending-statement",   perm: "reports_pending_sales_order:view" },
        ],
      },
      {
        key: "sales-dispatch-ops",
        label: "Sales Dispatch",
        items: [
          // Feb 2026 · Sub-menu deep-links into the Sales Orders sub-tabs.
          // Each carries a permission code (RBAC · fine-grained) plus a
          // `badgeKey` that maps to /api/dispatch-menu/counts.sales.<key>.
          { to: "/sales-orders?tab=slips",         label: "Packing Slips",        icon: PackageIcon,       testId: "nav-so-packing-slips",     perm: "sales_orders_slips:view",    badgeKey: "sales.packing_slips" },
          { to: "/sales-orders?tab=packing",       label: "Packing Lists",        icon: PackageIcon,       testId: "nav-so-packing-lists",     perm: "sales_orders_packing:view",  badgeKey: "sales.packing_lists" },
          { to: "/sales-orders?tab=challans",      label: "Delivery Challans",    icon: TruckIcon,         testId: "nav-so-delivery-challans", perm: "sales_orders_challans:view", badgeKey: "sales.delivery_challans" },
          { to: "/gst-invoices",                   label: "GST Invoices",         icon: FileTextIcon,      testId: "nav-gst-invoices",         perm: "gst_invoices:view" },
          { to: "/reports/dispatch-history",       label: "Dispatch History",     icon: TruckIcon,         testId: "nav-dispatch-history",    perm: "reports_dispatch_history:view" },
          { to: "/reports/packing-slip-backfill",  label: "Packing Slip Backfill", icon: FileTextIcon,      testId: "nav-ps-backfill",         perm: "reports_packing_slip_backfill:view" },
          { to: "/dispatch/excess-production",     label: "Excess Production",    icon: PackageIcon,       testId: "nav-excess-production",   perm: "dispatch_excess_production:view" },
          // Mar 3, 2026 · FG Inventory sub-block — Register (on-hand pivot)
          // + Stock Book (chronological movement per article). Both are
          // ledger-derived reports sourced from packing_slips ± delivery_challans.
          { to: "/sales/fg-stock-register",        label: "FG Stock Register",    icon: PackageIcon,       testId: "nav-fg-stock-register",   perm: "sales_fg_stock_register:view" },
          { to: "/sales/fg-stock-book",            label: "FG Stock Book",        icon: FileTextIcon,      testId: "nav-fg-stock-book",       perm: "sales_fg_stock_book:view" },
          { to: "/tally-sync",                     label: "Tally Sync",           icon: FileTextIcon,      testId: "nav-tally-sync",          perm: "tally_sync:view" },
          { to: "/catalogue",                      label: "Digital Catalogue",    icon: StorefrontIcon,    testId: "nav-catalogue",           perm: "catalogue:view" },
        ],
      },
    ],
  },
  {
    key: "exports-dispatch",
    label: "Exports & Dispatch",
    icon: PackageIcon,
    defaultPerm: "exports:view",
    // Sep 03, 2026 · Standardized grouped structure — mirrors the
    // Sales & Dispatch layout (Orders + Dispatch) for perfect symmetry.
    subGroups: [
      {
        key: "export-orders",
        label: "Export Orders",
        items: [
          { to: "/exports",                        label: "Export Orders",        icon: ClipboardTextIcon, testId: "nav-exports",              perm: "exports:view" },
          { to: "/export-proforma-invoices",       label: "Export Proforma",      icon: FileTextIcon,      testId: "nav-export-proforma",     perm: "export_proforma_invoices:view" },
          { to: "/reports/eso-reconciliation",     label: "ESO Reconciliation",   icon: ChartBarIcon,      testId: "nav-eso-reconciliation",  perm: "reports_eso_reconciliation:view" },
          { to: "/reports/export-so-reconciliation", label: "ESO Reconciliation (Item-wise)", icon: ChartBarIcon, testId: "nav-export-so-reconciliation", perm: "reports_eso_reconciliation:view" },
        ],
      },
      {
        key: "export-dispatch",
        label: "Export Dispatch",
        items: [
          { to: "/exports?tab=slips",              label: "Export Packing Slips", icon: PackageIcon,       testId: "nav-exp-packing-slips",   perm: "exports_slips:view" },
          { to: "/exports?tab=epl",                label: "Export Packing Lists", icon: PackageIcon,       testId: "nav-exp-packing-lists",   perm: "exports_epl:view",           badgeKey: "exports.packing_lists" },
          { to: "/exports?tab=dc",                 label: "Export Delivery Challans", icon: TruckIcon,      testId: "nav-exp-delivery-challans", perm: "exports_dc:view" },
          { to: "/exports?tab=invoices",           label: "Export Invoices",      icon: FileTextIcon,      testId: "nav-exp-invoices",         perm: "exports_invoices:view" },
          { to: "/export-gst-invoices",            label: "Export GST Invoices",  icon: FileTextIcon,      testId: "nav-export-gst-invoices", perm: "export_gst_invoices:view" },
          { to: "/exports?tab=report",             label: "Export Status Report", icon: ChartBarIcon,      testId: "nav-exp-report",           perm: "exports_report:view" },
          { to: "/reports/eso-dispatch-history",   label: "ESO Dispatch History", icon: TruckIcon,         testId: "nav-eso-dispatch-history", perm: "reports_eso_dispatch_history:view" },
        ],
      },
    ],
  },
  {
    key: "production-planning",
    label: "Production & Planning",
    icon: FactoryIcon,
    defaultPerm: "production_planning:view",
    // PMS.V3 · Phase 1A (Jun 2026) — department-wise regroup. PURE
    // navigation move: every item keeps its SAME route / component /
    // route-derived permission. No RBAC change, no screen change.
    subGroups: [
      {
        key: "prod-wo-cwo", label: "Work Order & CWO",
        items: [
          { to: "/production-orders", label: "Work Orders", icon: CalendarCheckIcon, testId: "nav-production-orders" },
          { to: "/child-work-orders", label: "Child Work Orders", icon: TreeStructureIcon, testId: "nav-child-work-orders" },
          { to: "/reports/pending-work-order", label: "Pending Work Order", icon: ClipboardTextIcon, testId: "nav-pending-work-order" },
        ],
      },
      {
        key: "prod-planning", label: "Planning",
        items: [
          { to: "/planning", label: "Production Planning", icon: CalendarCheckIcon, testId: "nav-planning" },
          { to: "/mrp", label: "MRP", icon: StackIcon, testId: "nav-mrp" },
          { to: "/material-requisitions", label: "Material Requisitions", icon: ClipboardTextIcon, testId: "nav-mrq" },
          { to: "/downtime", label: "Downtime", icon: WrenchIcon, testId: "nav-downtime" },
          { to: "/maintenance", label: "Maintenance", icon: WrenchIcon, testId: "nav-maintenance" },
        ],
      },
      {
        key: "prod-warping", label: "Warping",
        items: [
          { to: "/production/warping-entry", label: "Warping Production Entry", icon: PencilLineIcon, testId: "nav-warping-entry" },
          { to: "/reports/warping-production-register", label: "Warping Production Register", icon: ClipboardTextIcon, testId: "nav-warping-register" },
          { to: "/reports/warping-reconciliation", label: "Warping Reconciliation", icon: ChartBarIcon, testId: "nav-warping-reconciliation" },
        ],
      },
      {
        key: "prod-covering-spandex", label: "Covering / Spandex Warping",
        items: [
          { to: "/production/covering-spandex-entry", label: "Covering & Spandex Warping Production Entry", icon: PencilLineIcon, testId: "nav-covering-spandex-entry" },
          { to: "/reports/covering-production-register", label: "Covering Production Register", icon: ClipboardTextIcon, testId: "nav-covering-register" },
          { to: "/reports/spandex-warping-production-register", label: "Spandex Warping Register", icon: ClipboardTextIcon, testId: "nav-spandex-warping-register" },
          { to: "/reports/covering-production-reconciliation", label: "Covering Production Reconciliation", icon: ChartBarIcon, testId: "nav-covering-production-reconciliation" },
          { to: "/reports/covering-rm-reconciliation", label: "Covering Raw Material Reconciliation", icon: ChartBarIcon, testId: "nav-covering-rm-reconciliation" },
        ],
      },
      {
        key: "prod-weaving", label: "Weaving",
        items: [
          { to: "/entry", label: "Loom Production Entry & Register", icon: PencilLineIcon, testId: "nav-entry" },
          { to: "/production/weaving-consumption", label: "Weaving Consumption", icon: FactoryIcon, testId: "nav-weaving-consumption" },
          { to: "/reports/weaving-production-reconciliation", label: "Weaving Production Reconciliation", icon: ChartBarIcon, testId: "nav-weaving-production-reconciliation" },
          { to: "/reports/weaving-rm-reconciliation", label: "Weaving Raw Material Reconciliation", icon: ChartBarIcon, testId: "nav-weaving-rm-reconciliation" },
        ],
      },
      {
        key: "prod-finishing", label: "Finishing",
        items: [
          { to: "/production/finishing-entry", label: "Finishing Production", icon: PencilLineIcon, testId: "nav-finishing-entry", perm: "production_finishing_dyeing_entry:view" },
          { to: "/reports/finishing-production-reconciliation", label: "Finishing Production Reconciliation", icon: ChartBarIcon, testId: "nav-finishing-production-reconciliation" },
        ],
      },
      {
        key: "prod-dyeing", label: "Dyeing",
        items: [
          // Same existing permission as the old combined route (pinned so the
          // new route slug does NOT create a new permission / change access).
          { to: "/production/dyeing-entry", label: "Dyeing Production", icon: PencilLineIcon, testId: "nav-dyeing-entry", perm: "production_finishing_dyeing_entry:view" },
          { to: "/reports/dyeing-production-reconciliation", label: "Dyeing Production Reconciliation", icon: ChartBarIcon, testId: "nav-dyeing-production-reconciliation" },
        ],
      },
      {
        key: "prod-checking", label: "Checking",
        items: [
          { to: "/production/checking-entry", label: "Checking Production Entry", icon: PencilLineIcon, testId: "nav-checking-entry" },
          { to: "/reports/checking-production-register", label: "Checking Production Register", icon: ClipboardTextIcon, testId: "nav-checking-register" },
        ],
      },
      {
        key: "prod-material-wip", label: "Material & WIP",
        items: [
          { to: "/godown-balances", label: "Godown Balances", icon: WarehouseIcon, testId: "nav-godown-balances" },
          { to: "/stage-transfer", label: "Stage Transfer", icon: TruckIcon, testId: "nav-stage-transfer" },
          { to: "/production/material-transfer", label: "Material Transfer", icon: TruckIcon, testId: "nav-material-transfer", perm: "production_material_transfer:view" },
          { to: "/reports/rm-wastage-summary", label: "RM Wastage Summary", icon: ShieldCheckIcon, testId: "nav-rm-wastage-summary" },
        ],
      },
    ],
  },
  {
    key: "quality-assurance",
    label: "Quality Assurance",
    icon: ShieldCheckIcon,
    defaultPerm: "qa_running_weight:view",
    subGroups: [
      {
        key: "incoming-material-qa",
        label: "Incoming Material QA",
        items: [
          { to: "/qc-approval", label: "QA Approval", icon: ShieldCheckIcon, testId: "nav-qc-approval", perm: "qc_approval:view" },
        ],
      },
      {
        key: "running-weight",
        label: "Running Weight",
        items: [
          { to: "/qa/running-weight", label: "Weight Entry", icon: PencilLineIcon, testId: "nav-qa-running-weight", perm: "qa_running_weight:view" },
          { to: "/qa/running-weight-register", label: "Weight Register", icon: ClipboardTextIcon, testId: "nav-qa-running-weight-register", perm: "qa_running_weight_register:view" },
        ],
      },
      {
        // PMS.V3 · Phase 1A — In-Process Quality moved here from the old
        // Production Control subgroup. Same routes / derived permissions
        // (quality:view / rejections:view). Downtime & Maintenance stay
        // under Planning (NOT QA).
        key: "in-process-quality",
        label: "In-Process Quality",
        items: [
          { to: "/quality", label: "Quality", icon: ShieldCheckIcon, testId: "nav-quality" },
          { to: "/rejections", label: "Rejections", icon: ShieldCheckIcon, testId: "nav-rejections" },
        ],
      },
    ],
  },
  {
    key: "purchase-mod",
    label: "Purchase",
    icon: ShoppingCartIcon,
    defaultPerm: "inventory:view",
    // PMS.V3 · Phase 1A — Purchase split out of the old "Purchase &
    // Inventory" group into its own clearly-identifiable branch. Same
    // routes / components / derived permissions.
    items: [
      { to: "/purchase-requirements",           label: "Purchase Requisition",  icon: ClipboardTextIcon, testId: "nav-purchase-requirements" },
      { to: "/purchase-orders",                 label: "Purchase Order",        icon: ClipboardTextIcon, testId: "nav-purchase-orders" },
      { to: "/reports/pending-purchase-order",  label: "Pending Purchase Order",icon: ClipboardTextIcon, testId: "nav-pending-purchase" },
      { to: "/inventory/purchase-receipt",      label: "GRN",                   icon: TruckIcon,         testId: "nav-purchase-receipt" },
      { to: "/inventory/purchase-returns",      label: "Purchase Return",       icon: TruckIcon,         testId: "nav-purchase-returns" },
    ],
  },
  {
    key: "inventory-mod",
    label: "Inventory",
    icon: WarehouseIcon,
    defaultPerm: "inventory:view",
    // PMS.V3 · Phase 1A — Inventory split into its own branch (Inventory
    // + Material Movement sub-groups). Legacy Inventory trio KEPT visible
    // per Owner decision. Same routes / components / permissions.
    subGroups: [
      {
        key: "inv-items",
        label: "Inventory",
        items: [
          { to: "/inventory/v2?tab=book",       label: "Stock Book",           icon: WarehouseIcon, testId: "nav-inv-stock-book", perm: "stock_engine:view" },
          { to: "/inventory/v2?tab=register",   label: "Stock Register",       icon: WarehouseIcon, testId: "nav-inv-stock-register", perm: "stock_engine:view" },
          { to: "/inventory/v2?tab=ledger",     label: "Stock Ledger",         icon: FileTextIcon,  testId: "nav-inv-stock-ledger", perm: "stock_engine:view" },
          { to: "/material-traceability",       label: "Material Traceability",icon: PackageIcon,   testId: "nav-inv-material-traceability", perm: "material_traceability:view" },
          { to: "/inventory/v2?tab=reconcile",  label: "Stock Reconciliation", icon: WarehouseIcon, testId: "nav-inv-stock-reconciliation", perm: "stock_engine:view" },
          { to: "/inventory/fg-reconciliation", label: "FG \u2194 Dispatch Reconciliation", icon: ChartBarIcon, testId: "nav-fg-reconciliation", perm: "fg_dispatch_reconciliation:view" },
          { to: "/inventory/opening-stock",     label: "Opening Stock Entry",  icon: PackageIcon,   testId: "nav-opening-stock", perm: "opening_stock:view", hiddenWhenLocked: true },
          { to: "/inventory/excess",            label: "Excess Inventory",     icon: StackIcon,     testId: "nav-excess-inventory" },
          { to: "/inventory/low-stock",         label: "Low Stock Alerts",     icon: WarehouseIcon, testId: "nav-low-stock" },
          { to: "/inventory/physical-verification", label: "Physical Verification", icon: ClipboardTextIcon, testId: "nav-physical-verification" },
          { to: "/inventory/reservations",      label: "Stock Reservation",    icon: LockKeyIcon,   testId: "nav-stock-reservation" },
          { to: "/inventory/returnable-challans", label: "Pending Returnable Challans", icon: StackIcon, testId: "nav-returnable-challans" },
          { to: "/quality/traceability",        label: "Batch Traceability",   icon: StackIcon,     testId: "nav-batch-traceability" },
          { to: "/inventory",                   label: "Inventory Ledger (Legacy)", icon: PackageIcon,   testId: "nav-inventory" },
          { to: "/stock-register",              label: "Stock Register (Legacy)",   icon: WarehouseIcon, testId: "nav-stock-register" },
          { to: "/inventory/stock-book",        label: "Stock Ledger (Legacy)",     icon: FileTextIcon,  testId: "nav-stock-ledger" },
        ],
      },
      {
        key: "inv-movement",
        label: "Material Movement",
        items: [
          { to: "/material-issue/mi",           label: "MI Register",                icon: PackageIcon, testId: "nav-material-issue-mi" },
          { to: "/material-issue",              label: "Material Issue Request (MIR)", icon: PackageIcon, testId: "nav-material-issue" },
          { to: "/inventory/bin-transfer",      label: "Material Transfer (Bin)",    icon: TruckIcon,   testId: "nav-bin-transfer" },
          { to: "/inventory/covering-rm-transfer", label: "Material Transfer (RM → Covering)", icon: TruckIcon, testId: "nav-crt" },
          { to: "/inventory/scrap",             label: "Stock Adjustment (Scrap)",   icon: WrenchIcon,  testId: "nav-scrap-entry" },
          { to: "/inventory/fg-warehouse-receipt", label: "FG Warehouse Receipt",    icon: PackageIcon, testId: "nav-fg-warehouse-receipt", perm: "fg_warehouse_receipt:view" },
        ],
      },
    ],
  },
  {
    key: "master-data",
    label: "Master Data",
    icon: DatabaseIcon,
    defaultPerm: "masters:view",
    // Feb 2026 · Four collapsible sub-groups so a 20-item master data
    // list stays scannable. Each sub-group is a first-class collapsible
    // section rendered inside the parent Master Data group.
    subGroups: [
      {
        key: "product-masters",
        label: "Product Masters",
        items: [
          { to: "/articles",                   label: "FG Master",            icon: PackageIcon,   testId: "nav-articles" },
          { to: "/sf-master",                  label: "SF Master",            icon: PackageIcon,   testId: "nav-sf-master" },
          { to: "/material-traceability",      label: "Material Traceability",icon: PackageIcon,   testId: "nav-material-traceability", perm: "material_traceability:view" },
          { to: "/bom",                        label: "BOM Master",           icon: StackIcon,     testId: "nav-bom" },
          { to: "/masters/article-types",      label: "Product Category",     icon: StackIcon,     testId: "nav-article-types" },
          { to: "/masters/items",              label: "Product Master",       icon: PackageIcon,   testId: "nav-items" },
          { to: "/masters/colors",             label: "Colour Master",        icon: DatabaseIcon,  testId: "nav-colors" },
        ],
      },
      {
        key: "inventory-masters",
        label: "Inventory Masters",
        items: [
          { to: "/raw-materials",              label: "RM Master",            icon: PackageIcon,   testId: "nav-raw-materials" },
          { to: "/masters/item-categories",    label: "Item Category",        icon: StackIcon,     testId: "nav-item-categories" },
          { to: "/masters/item-groups",        label: "Item Group",           icon: StackIcon,     testId: "nav-item-groups" },
          { to: "/masters/denier-count-options", label: "Denier / Count",     icon: DatabaseIcon,  testId: "nav-denier" },
          { to: "/masters/tpm-roto-options",   label: "TPM / ROTO",           icon: DatabaseIcon,  testId: "nav-tpm-roto" },
          { to: "/masters/godowns",            label: "Godown Master",        icon: BuildingsIcon, testId: "nav-godown-master" },
          { to: "/masters/bin-locations",      label: "Bin Location",         icon: WarehouseIcon, testId: "nav-bin-locations" },
          { to: "/masters/uom-masters",        label: "UOM Master",           icon: DatabaseIcon,  testId: "nav-uom-masters" },
          { to: "/masters/hsn-codes",          label: "HSN Master",           icon: FileTextIcon,  testId: "nav-hsn-codes" },
          { to: "/masters/reorder-policies",   label: "Reorder Policy",       icon: WarehouseIcon, testId: "nav-reorder-policies" },
          { to: "/masters/number-series",      label: "Number Series",        icon: FileTextIcon,  testId: "nav-number-series" },
        ],
      },
      {
        // Feb 2026 · 5th sub-group — dedicated Financial Masters cluster
        // for Currency + FX Rates + Tax so finance/procurement teams find
        // them instantly without scrolling past inventory codes.
        key: "financial-masters",
        label: "Financial Masters",
        items: [
          { to: "/masters/currencies",         label: "Currency Master",      icon: CurrencyInrIcon, testId: "nav-currencies" },
          { to: "/masters/accounting-year",    label: "Accounting Year",      icon: CalendarCheckIcon, testId: "nav-accounting-year" },
          { to: "/masters/fx-rates",           label: "↳ FX Rates",           icon: CurrencyInrIcon, testId: "nav-fx-rate-master" },
          { to: "/masters/tax-masters",        label: "Tax Master",           icon: FileTextIcon,  testId: "nav-tax-masters" },
          { to: "/masters/supplier-price-list", label: "Supplier Price List", icon: CurrencyInrIcon, testId: "nav-supplier-price-list" },
          { to: "/masters/rm-alternate-approvals", label: "RM Alternate Approvals", icon: ShieldCheckIcon, testId: "nav-rm-alternate-approvals", perm: "rm_alternate:view" },
        ],
      },
      {
        key: "production-masters",
        label: "Production Masters",
        items: [
          { to: "/masters/machines",           label: "Machine Master",       icon: DatabaseIcon,  testId: "nav-masters" },
          { to: "/masters/machine-types",      label: "Machine Types",        icon: DatabaseIcon,  testId: "nav-machine-types" },
          { to: "/masters/machine-type-options", label: "Machine Category",   icon: DatabaseIcon,  testId: "nav-machine-categories" },
          { to: "/masters/departments",        label: "Departments",          icon: BuildingsIcon, testId: "nav-departments" },
          { to: "/masters/shifts",             label: "Shifts",               icon: DatabaseIcon,  testId: "nav-shifts" },
          { to: "/masters/operators",          label: "Operators",            icon: DatabaseIcon,  testId: "nav-operators" },
          { to: "/masters/downtime-reasons",   label: "Downtime Reasons",     icon: FileTextIcon,  testId: "nav-downtime-reasons" },
          { to: "/masters/defects",            label: "Defects",              icon: FileTextIcon,  testId: "nav-defects" },
          { to: "/masters/routing-stages",     label: "Routing Stages",       icon: StackIcon,     testId: "nav-routing-stages" },
          { to: "/masters/approval-matrix",    label: "Approval Matrix",      icon: LockKeyIcon,   testId: "nav-approval-matrix" },
        ],
      },
      {
        key: "partner-masters",
        label: "Partner Masters",
        items: [
          { to: "/masters/customers",          label: "Customers",            icon: DatabaseIcon,  testId: "nav-customers" },
          { to: "/masters/suppliers",          label: "Suppliers",            icon: DatabaseIcon,  testId: "nav-suppliers" },
          { to: "/masters/brands",             label: "Brand",                icon: DatabaseIcon,  testId: "nav-brands" },
          { to: "/masters/transporters",       label: "Transporter",          icon: TruckIcon,     testId: "nav-transporters" },
          { to: "/masters/payment-terms",      label: "Payment Terms",        icon: FileTextIcon,  testId: "nav-payment-terms" },
        ],
      },
    ],
  },
  {
    key: "reports-mod",
    label: "Reports",
    icon: ChartBarIcon,
    subGroups: [
      {
        key: "reports-reconciliation", label: "Reconciliation",
        items: [
          { to: "/reports/wo-consolidated-reconciliation", label: "Consolidated WO Reconciliation", icon: ChartBarIcon, testId: "nav-wo-consolidated-reconciliation" },
        ],
      },
    ],
  },
  {
    key: "reports-system",
    label: "Administrator",
    icon: ChartBarIcon,
    items: [
      { to: "/management-approvals", label: "Management Authorisation", icon: ShieldCheckIcon, testId: "nav-mgmt-approvals", adminOnly: true, badgeKey: "approvals.total" },
      { to: "/reports", label: "Reports & Analytics", icon: ChartBarIcon, testId: "nav-reports" },
      // Sep 03, 2026 · Permission Reference Guide — in-app cheat-sheet
      // for the 11 Core Permission Categories used across the MES.
      { to: "/reports/permission-reference", label: "Permission Reference", icon: LockKeyIcon, testId: "nav-permission-reference", perm: "reports:view" },
      { to: "/feedback", label: "User Feedback", icon: ChatCircleTextIcon, testId: "nav-feedback" },
      { to: "/settings/company", label: "Company Profile", icon: BuildingsIcon, testId: "nav-company-settings", adminOnly: true },
      { to: "/settings/holiday-calendar", label: "Holiday Calendar", icon: ClipboardTextIcon, testId: "nav-holiday-calendar", adminOnly: true },
      { to: "/settings/tnc-templates", label: "T&C Templates", icon: FileTextIcon, testId: "nav-tnc-templates", adminOnly: true },
      // Feb 2026 · Users & Roles surfaced as its own admin-only entry so
      // System Administrators reach it in one click instead of hunting
      // for the "Users" tab inside Master Data.
      { to: "/masters/users", label: "Users & Roles", icon: UserIcon, testId: "nav-users-roles", adminOnly: true },
      { to: "/masters/roles", label: "Role Master", icon: LockKeyIcon, testId: "nav-role-master", adminOnly: true },
      { to: "/audit-log", label: "Activity Trail", icon: ClipboardTextIcon, testId: "nav-audit-log", adminOnly: true },
      { to: "/reports/reversal-audit", label: "Reversal Audit", icon: WarningIcon, testId: "nav-reversal-audit" },
      { to: "/settings/roles", label: "Roles & Permissions", icon: LockKeyIcon, testId: "nav-rbac", adminOnly: true },
    ],
  },
];

// Flatten a group's items — includes items nested under sub-groups so
// filters/searches work uniformly regardless of nesting depth.
function flatItems(group) {
  if (Array.isArray(group.items)) return group.items;
  if (Array.isArray(group.subGroups)) return group.subGroups.flatMap(sg => sg.items || []);
  return [];
}

// Which group owns the current URL? Longest-prefix match against every
// item's `to`. Used to auto-expand the correct parent group on nav.
// Query strings are stripped so `/sales-orders?tab=slips` still matches
// the `/sales-orders` sub-menu entry.
function findActiveGroupKey(pathname) {
  let bestKey = null;
  let bestLen = -1;
  for (const g of GROUPS) {
    for (const it of flatItems(g)) {
      const path = (it.to || "").split("?")[0];
      if (pathname === path || pathname.startsWith(path + "/")) {
        if (path.length > bestLen) { bestLen = path.length; bestKey = g.key; }
      }
    }
  }
  return bestKey;
}

// Also compute the active sub-group key (if any) so it opens automatically.
function findActiveSubGroupKey(pathname) {
  let bestKey = null;
  let bestLen = -1;
  for (const g of GROUPS) {
    if (!g.subGroups) continue;
    for (const sg of g.subGroups) {
      for (const it of (sg.items || [])) {
        const path = (it.to || "").split("?")[0];
        if (pathname === path || pathname.startsWith(path + "/")) {
          if (path.length > bestLen) { bestLen = path.length; bestKey = sg.key; }
        }
      }
    }
  }
  return bestKey;
}

function isItemActive(location, item) {
  const path = (item.to || "").split("?")[0];
  const wantedTab = (item.to || "").split("?")[1]?.split("=")[1] || null;
  const currentTab = new URLSearchParams(location.search).get("tab");
  if (item.exact) return location.pathname === path;
  // When the sidebar item deep-links to a specific ?tab=, only mark
  // active if that tab matches. Otherwise fall back to plain path match.
  if (wantedTab) return location.pathname === path && currentTab === wantedTab;
  // The "base" nav item (no ?tab=) is active only when there's no tab
  // in the URL — so the parent doesn't stay lit when a child tab is open.
  const hasSiblingWithTab = false; // (evaluated at render, see below)
  return (location.pathname === path || location.pathname.startsWith(path + "/"))
         && (!hasSiblingWithTab || !currentTab);
}


// Sep 2026 - route-level RBAC. The sidebar filter and the router guard resolve
// a route through the SAME functions here, so they cannot disagree.
export function navSlug(to) {
  const [path, query = ""] = String(to || "").split("?");
  let s = path.replace(/^\//, "").replace(/-/g, "_").replace(/\//g, "_").replace(/\s+/g, "_").toLowerCase();
  const tabMatch = query.split("&").find(q => q.startsWith("tab="));
  if (tabMatch) s += "_" + tabMatch.slice(4).replace(/-/g, "_");
  s = s.replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
  return s || "root";
}

function flattenNav() {
  const out = [];
  const push = (it, group) => {
    if (!it || !it.to) return;
    const derived = navSlug(it.to) + ":view";
    out.push({
      to: it.to,
      slug: navSlug(it.to),
      pathSlug: navSlug(String(it.to).split("?")[0]),
      perm: it.perm !== undefined ? it.perm : (derived || (group && group.defaultPerm)),
      adminOnly: !!it.adminOnly,
      label: it.label || it.to,
    });
  };
  TOP_LEVEL.forEach(it => push(it, null));
  GROUPS.forEach(g => {
    (g.items || []).forEach(it => push(it, g));
    (g.subGroups || []).forEach(sg => (sg.items || []).forEach(it => push(it, g)));
  });
  return out;
}

let _navIndex = null;
// Returns null when a route is absent from the sidebar config - the caller must
// ALLOW and log in that case, never deny.
export function resolveNavRule(pathname, search) {
  if (!_navIndex) _navIndex = flattenNav();
  const full = navSlug(String(pathname || "") + String(search || ""));
  const pathOnly = navSlug(String(pathname || ""));
  return _navIndex.find(e => e.slug === full)
      || _navIndex.find(e => e.pathSlug === pathOnly)
      || null;
}

// V28-003 · Permission-driven post-login landing resolver. Walks the SAME
// flattened sidebar config (in sidebar order) and returns the first navigable
// route the caller holds VIEW for. Skips admin-only items and the Dashboard
// "/" entry (now the real `main_dashboard:view`, formerly the phantom
// `root:view`) — Dashboard is never auto-landed for non-admins; Admins reach
// it via their implicit bypass. Returns null when the user has no authorized
// navigable route. Reuses the existing `can(module, action)` RBAC truth.
export function firstAuthorizedRoute(can) {
  if (typeof can !== "function") return null;
  const items = _navIndex || (_navIndex = flattenNav());
  for (const e of items) {
    if (e.adminOnly) continue;
    if (e.slug === "root") continue; // Dashboard "/" (main_dashboard:view) — never auto-landed
    if (!e.perm) continue;
    const [mod, act] = String(e.perm).split(":");
    if (mod && act && can(mod, act)) return e.to;
  }
  return null;
}

// V28-003A · Access-Denied content for an authorized-but-forbidden route.
// Rendered INSIDE the Layout shell (via RouteGuard around the Outlet) so the
// sidebar, header and Sign out stay intact and the user can recover by
// clicking any other permitted menu. Does NOT grant any permission.
export function AccessDenied({ label, perm }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="access-denied-state">
      <div className="max-w-md text-center">
        <div className="text-4xl mb-3">&#128274;</div>
        <h1 className="text-xl font-semibold text-white mb-2">No access to {label}</h1>
        <p className="text-sm text-zinc-400">
          Your role does not have permission to open this screen. Pick another
          menu from the sidebar to continue, or sign out from the top bar.
        </p>
        <p className="text-xs text-zinc-500 mt-3 font-mono">{perm}</p>
        <p className="text-xs text-zinc-500 mt-3">
          Ask an administrator to grant it on Settings &rarr; Roles &amp; Permissions.
        </p>
      </div>
    </div>
  );
}

// V28-003A · Per-route permission gate that runs at the CONTENT level (around
// the Layout Outlet), NOT around the whole shell. Preserves the exact prior
// Protected authorization rules (administrator / not-ready bypass, admin-only
// routes, module:action VIEW check, unregistered routes ALLOW+log) but renders
// AccessDenied inside the shell instead of replacing it. No permission bypass.
export function RouteGuard({ children }) {
  const location = useLocation();
  const { can, ready, administrator } = useRbac();
  if (administrator || !ready) return children;
  const rule = resolveNavRule(location.pathname, location.search);
  if (!rule) {
    console.warn("[rbac] route not in nav config, allowing:", location.pathname + location.search);
    return children;
  }
  if (rule.adminOnly) return <AccessDenied label={rule.label} perm="Administrator only" />;
  if (rule.perm) {
    const [mod, act] = String(rule.perm).split(":");
    if (mod && act && !can(mod, act)) return <AccessDenied label={rule.label} perm={rule.perm} />;
  }
  return children;
}

export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const alertsData = useAlerts();
  const alertCount = alertsData.count;
  const alertCritical = alertsData.critical;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("mes_sidebar_collapsed") === "1");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Feb 27, 2026 · Opening Stock Entry lockdown flag — polled every 60 s
  // so non-Admin users see the sidebar item disappear the moment an
  // Administrator flips the toggle in Reports & System.
  const [openingLocked, setOpeningLocked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const api = (await import("@/lib/api")).default;
        const { data } = await api.get("/opening-stock-entries/lock-status");
        if (!cancelled) setOpeningLocked(!!data?.locked);
      } catch { /* ignore — treat as unlocked */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Per-user open group state — persisted so operators keep their
  // preferred layout across sessions.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("mes_sidebar_open_groups");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { "sales-dispatch": true }; // sensible default
  });
  // Feb 2026 · Dispatch menu counts — pending + overdue for Packing Slips,
  // Packing Lists, Delivery Challans on both Sales and Exports sides.
  // Refreshed on mount + every 60s so the badges stay honest without a
  // full page reload. Missing/failed fetch simply hides the badges.
  const [menuCounts, setMenuCounts] = useState({ sales: {}, exports: {} });
  useEffect(() => {
    let cancelled = false;
    // Sep 03, 2026 · Silence 403 noise — `/approvals/count` is
    // Administrator-only (see `routes/approvals.py::_admin_only`). Skip
    // the fetch for every other role so operator consoles stay clean.
    const isAdmin = (user?.role || "") === "Administrator";
    const fetchCounts = async () => {
      try {
        const api = (await import("@/lib/api")).default;
        // Feb 2026 · Merge Approvals total into the same menuCounts blob
        // so the sidebar badge renderer can pick it up under the
        // `approvals.total` key without a second code path.
        const [countsRes, apRes] = await Promise.all([
          api.get("/dispatch-menu/counts"),
          isAdmin
            ? api.get("/approvals/count").catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        const merged = countsRes.data || { sales: {}, exports: {} };
        if (apRes.data) merged.approvals = { total: { pending: apRes.data.total || 0, overdue: 0 } };
        if (!cancelled) setMenuCounts(merged);
      } catch { /* silent — badges just don't render */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.role]);
  const getBadge = (badgeKey) => {
    if (!badgeKey) return null;
    const [group, doc] = badgeKey.split(".");
    return menuCounts?.[group]?.[doc] || null;
  };
  useEffect(() => { localStorage.setItem("mes_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { localStorage.setItem("mes_sidebar_open_groups", JSON.stringify(openGroups)); }, [openGroups]);

  // Auto-expand the group that owns the current URL. This is additive —
  // groups the user manually opened stay open.
  const activeGroupKey = useMemo(() => findActiveGroupKey(location.pathname), [location.pathname]);
  const activeSubGroupKey = useMemo(() => findActiveSubGroupKey(location.pathname), [location.pathname]);
  useEffect(() => {
    setOpenGroups(g => {
      const next = { ...g };
      if (activeGroupKey && !next[activeGroupKey]) next[activeGroupKey] = true;
      if (activeSubGroupKey && !next[activeSubGroupKey]) next[activeSubGroupKey] = true;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey, activeSubGroupKey]);

  const toggleGroup = (key) => setOpenGroups(g => ({ ...g, [key]: !g[key] }));

  // PMS.V3 Phase 1B · Compact Dark visual polish — restrained Level-1 module
  // icon accents (scan aid only; NOT per-item rainbow). Visual-only.
  const GROUP_ACCENT = {
    "sales-dispatch": "text-blue-400",
    "exports-dispatch": "text-violet-400",
    "production-planning": "text-sky-400",
    "quality-assurance": "text-teal-400",
    "purchase-mod": "text-amber-400",
    "inventory-mod": "text-cyan-400",
    "master-data": "text-indigo-400",
    "reports-system": "text-slate-300",
  };
  // Phase 1B.1 · restrained semantic accents for the top-level standalone icons.
  const TOP_ACCENT = {
    "/": "text-blue-400",
    "/live": "text-emerald-400",
    "/sales-dashboard": "text-indigo-400",
    "/dashboards/purchase": "text-amber-400",
    "/dashboards/inventory": "text-cyan-400",
    "/ai": "text-violet-400",
  };

  // Feb 2026 · Mobile Executive Mode — hide the entire sidebar + topbar
  // and render Outlet + BottomNav only. The MobileGuard already
  // redirects non-dashboard URLs to /sales-dashboard, so Outlet always
  // renders a permitted view here.
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[var(--bg-0)]" data-testid="mobile-layout">
        <main className="flex flex-col min-h-screen">
          <div className="flex-1 overflow-auto" style={{ paddingBottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}>
            <RouteGuard><Outlet /></RouteGuard>
          </div>
        </main>
        <BottomNav />
        <ReadOnlyBadge />
      </div>
    );
  }

  // Feb 2026 · Extracted nav-item renderer so it can be reused for both
  // flat groups AND sub-grouped items (Master Data). Keeps the badge
  // rendering + collapsed-rail dot indicator + active state consistent.
  const renderNavItem = (it, loc) => {
    const Icon = it.icon;
    const active = isItemActive(loc, it);
    const badge = getBadge(it.badgeKey);
    const pending = badge?.pending || 0;
    const overdue = badge?.overdue || 0;
    return (
      <NavLink
        key={it.to}
        to={it.to}
        data-testid={it.testId}
        title={collapsed ? `${it.label}${pending ? ` · ${pending} pending${overdue ? ` · ${overdue} overdue` : ""}` : ""}` : undefined}
        className={`flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2 rounded-sm text-sm border border-transparent transition-colors ${
          active ? "bg-[var(--bg-2)] text-white shadow-[inset_2px_0_0_0_var(--accent)]" : "text-zinc-300 hover:text-white hover:bg-[var(--bg-2)]/60"
        }`}
      >
        <div className="relative shrink-0">
          <Icon size={15} weight={active ? "fill" : "regular"} className={active ? "text-[var(--accent)]" : "text-zinc-500"} />
          {collapsed && pending > 0 && (
            <span
              data-testid={`${it.testId}-badge-dot`}
              className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${overdue > 0 ? "bg-red-500" : "bg-amber-500"}`}
            />
          )}
        </div>
        {!collapsed && (
          <>
            <span className="font-medium leading-tight line-clamp-2 flex-1">{it.label}</span>
            {pending > 0 && (
              <span className="flex items-center gap-1">
                {overdue > 0 && (
                  <span
                    data-testid={`${it.testId}-badge-overdue`}
                    title={`${overdue} overdue`}
                    className="inline-flex items-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-mono font-bold bg-red-500 text-white"
                  >{overdue}</span>
                )}
                <span
                  data-testid={`${it.testId}-badge-pending`}
                  title={`${pending} pending`}
                  className={`inline-flex items-center min-w-[18px] h-4 px-1.5 rounded-full text-[9px] font-mono font-bold ${overdue > 0 ? "bg-red-500/20 text-red-300 border border-red-500/40" : "bg-amber-500/20 text-amber-300 border border-amber-500/40"}`}
                >{pending > 99 ? "99+" : pending}</span>
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  // Feb 2026 · DB-backed RBAC — items with an explicit `perm:` key are
  // filtered against `/rbac/me/permissions` via `hasPerm()`. Aug 31, 2026:
  // when a sub-menu item has NO explicit `perm`, we auto-derive one from its
  // route so the sidebar-sync'd RBAC grid toggles hit the correct entry
  // (checking "Sales Orders" grants `/sales-orders`). Keep the slug rule in
  // lockstep with backend/routes/rbac.py::_slug().
  // V28-003B · Lifted to component scope so TOP_LEVEL and GROUPS share the
  // SAME permission-visibility principle (no second permission system).
  const _slug = navSlug;
  const filterItem = (it, group) => {
    if (it.adminOnly && user?.role !== "Administrator") return false;
    const derived = it.to ? `${_slug(it.to)}:view` : null;
    const eff = it.perm !== undefined ? it.perm : (derived || group?.defaultPerm);
    if (eff && typeof hasPerm === "function" && !hasPerm(eff) && user?.role !== "Administrator") {
      return false;
    }
    if (it.hiddenWhenLocked && openingLocked && user?.role !== "Administrator") return false;
    return true;
  };
  const visibleGroups = GROUPS.map(g => {
    if (Array.isArray(g.subGroups)) {
      const subGroups = g.subGroups
        .map(sg => ({ ...sg, items: (sg.items || []).filter(it => filterItem(it, g)) }))
        .filter(sg => sg.items.length > 0);
      return { ...g, subGroups, items: [] };
    }
    return { ...g, items: (g.items || []).filter(it => filterItem(it, g)) };
  }).filter(g => (g.subGroups?.length || 0) > 0 || (g.items?.length || 0) > 0);

  // V28-003B · TOP_LEVEL now honours the same hasPerm filter as GROUPS.
  // Every TOP_LEVEL item now carries an EXPLICIT canonical perm (Phase 1:
  // Dashboard "/" → `main_dashboard:view`, replacing the old phantom
  // `root:view`). No role currently holds `main_dashboard:view`, so
  // Dashboard stays Administrator-only — identical to the approved
  // V28-003 landing behaviour (non-admins land on their first authorized
  // route, not Dashboard). No permission is broadened.
  const visibleTopLevel = TOP_LEVEL.filter(n => filterItem(n, null));

  return (
    <div className="min-h-screen flex bg-[var(--bg-0)]">
      <aside className={`${collapsed ? "w-16" : "w-64"} transition-all duration-200 border-r border-[var(--border)] bg-[var(--bg-1)] flex flex-col sticky top-0 h-screen`}>
        <div className="px-3 py-5 border-b border-[var(--border)] flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm bg-[var(--accent)] flex items-center justify-center shrink-0">
            <FactoryIcon size={18} weight="bold" className="text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm tracking-tight text-white truncate">NARROW FABRIC- PMS.V2.8</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Elastic Weaving</div>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {/* Top-level (ungrouped) items — V28-003B: permission-filtered */}
          {visibleTopLevel.map(n => {
            const Icon = n.icon;
            const active = isItemActive(location, n);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                title={collapsed ? n.label : undefined}
                className={`flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2 rounded-sm text-sm border border-transparent transition-colors ${
                  active ? "bg-[var(--bg-2)] text-white shadow-[inset_2px_0_0_0_var(--accent)]" : "text-zinc-300 hover:text-white hover:bg-[var(--bg-2)]/60"
                }`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} className={TOP_ACCENT[n.to] || "text-zinc-500"} />
                {!collapsed && <span className="font-semibold truncate">{n.label}</span>}
              </NavLink>
            );
          })}

          {/* Grouped sections */}
          {visibleGroups.map((g) => {
            const GIcon = g.icon;
            const open = !!openGroups[g.key];
            const isActiveGroup = activeGroupKey === g.key;
            return (
              <div key={g.key} className="mt-2">
                {collapsed ? (
                  /* Collapsed rail — render the group's icon as a divider
                     with hover-tooltip; items pop out one at a time in
                     the child list below with icon-only rendering. */
                  <div className="flex items-center justify-center py-1.5" title={g.label}>
                    <GIcon size={15} weight="bold" className={isActiveGroup ? (GROUP_ACCENT[g.key] || "text-[var(--accent)]") : "text-zinc-500"} />
                  </div>
                ) : (
                  <button
                    data-testid={`nav-group-${g.key}`}
                    onClick={() => toggleGroup(g.key)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                      isActiveGroup ? "text-white" : "text-zinc-200 hover:text-white"
                    }`}
                    aria-expanded={open}
                  >
                    <GIcon size={15} weight={isActiveGroup ? "fill" : "bold"} className={GROUP_ACCENT[g.key] || "text-[var(--accent)]"} />
                    <span className="flex-1 text-left truncate">{g.label}</span>
                    {open ? <CaretDownIcon size={11} weight="bold" className="text-zinc-500" /> : <CaretRightIcon size={11} weight="bold" className="text-zinc-500" />}
                  </button>
                )}

                {(open || collapsed) && (
                  <div className={collapsed ? "" : "pl-2 border-l border-[var(--border)] ml-4 mt-0.5 space-y-0.5"}>
                    {/* Feb 2026 · Sub-group aware render. If the group
                        defines `subGroups`, each renders as its own
                        collapsible section (Master Data uses this).
                        Otherwise the group's flat `items` render as
                        before. */}
                    {Array.isArray(g.subGroups) && g.subGroups.length > 0
                      ? g.subGroups.map(sg => {
                          const sgOpen = !!openGroups[sg.key];
                          const isActiveSub = activeSubGroupKey === sg.key;
                          if (collapsed) {
                            // On collapsed rail, flatten sub-group items —
                            // no room for nested chevrons.
                            return sg.items.map(it => renderNavItem(it, location));
                          }
                          return (
                            <div key={sg.key} className="mt-1">
                              <button
                                data-testid={`nav-subgroup-${sg.key}`}
                                onClick={() => toggleGroup(sg.key)}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold transition-colors ${
                                  isActiveSub ? "text-[var(--accent)]" : "text-zinc-300 hover:text-white"
                                }`}
                                aria-expanded={sgOpen}
                              >
                                <span className="flex-1 text-left truncate">{sg.label}</span>
                                {sgOpen ? <CaretDownIcon size={10} weight="bold" className="text-zinc-600" /> : <CaretRightIcon size={10} weight="bold" className="text-zinc-600" />}
                              </button>
                              {sgOpen && (
                                <div className="pl-1 space-y-0.5">
                                  {sg.items.map(it => renderNavItem(it, location))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      : g.items.map(it => renderNavItem(it, location))}
                    {g.key === "reports-system" && <Ldc01NavEntry collapsed={collapsed} />}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-2 border-t border-[var(--border)] space-y-1">
          <button
            onClick={() => setCollapsed(c => !c)}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-full flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-3"} py-2 rounded-sm text-sm text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]`}
          >
            {collapsed ? <CaretDoubleRightIcon size={14} weight="bold" /> : <CaretDoubleLeftIcon size={14} weight="bold" />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </button>
          <div className={`flex items-center gap-3 ${collapsed ? "justify-center px-1" : "px-2"} py-2 rounded-sm bg-[var(--bg-2)]`}>
            <div className="w-8 h-8 rounded-sm bg-[var(--accent)]/20 flex items-center justify-center shrink-0">
              <UserIcon size={16} className="text-[var(--accent)]" />
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate" data-testid="user-name">{user?.name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{user?.role}</div>
                </div>
                {user?.id && <MySignatureButton userId={user.id} />}
                <button onClick={logout} data-testid="logout-btn" className="p-1.5 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10" title="Logout">
                  <SignOutIcon size={16} />
                </button>
              </>
            )}
            {collapsed && (
              <button onClick={logout} data-testid="logout-btn" className="p-1.5 rounded-sm text-zinc-400 hover:text-red-400" title="Logout">
                <SignOutIcon size={14} />
              </button>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-[var(--border)] bg-[var(--bg-1)]/80 backdrop-blur-xl sticky top-0 z-10 flex items-center px-6 gap-4">
          <GlobalSearch />
          <div className="flex items-center gap-2">
            <span className="overline">Live</span>
            <span className="w-2 h-2 rounded-full bg-emerald-500 status-pulse" />
          </div>
          <button onClick={toggle} data-testid="theme-toggle" title={theme === "dark" ? "Switch to light" : "Switch to dark"}
            className="p-2 rounded-sm hover:bg-[var(--bg-2)] text-zinc-400 hover:text-white border border-transparent hover:border-[var(--border)]">
            {theme === "dark" ? <SunIcon size={16} weight="bold" /> : <MoonIcon size={16} weight="bold" />}
          </button>
          <button onClick={() => setFeedbackOpen(true)} data-testid="header-feedback-btn" title="Send feedback about the app"
            className="h-9 px-3 rounded-sm border border-[var(--border)] hover:border-[var(--accent)]/60 hover:bg-[var(--bg-2)] text-zinc-300 hover:text-white text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5 transition">
            <ChatCircleTextIcon size={14} weight="bold" />Feedback
          </button>
          {/* Feb 2026 · Authorization alerts — new topbar bell with unread
              badge + dropdown. Separate from the alarms bell so admins see
              approval-flow pings from any page. */}
          <NotificationsBell />
          <button className="relative p-2 rounded-sm hover:bg-[var(--bg-2)] text-zinc-400" data-testid="notifications-btn" onClick={() => setAlertsOpen(true)}>
            <BellIcon size={16} />
            {alertCount > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-mono font-bold flex items-center justify-center ${alertCritical > 0 ? "bg-red-500 text-white" : "bg-amber-500 text-black"}`}>
                {alertCount > 99 ? "99+" : alertCount}
              </span>
            )}
          </button>
        </header>
        <AlertsDrawer open={alertsOpen} onOpenChange={setAlertsOpen} data={alertsData} />
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        <StickyHorizontalScrollbar />
        <div className="flex-1 overflow-auto pb-1">
          <RouteGuard><Outlet /></RouteGuard>
        </div>
      </main>
    </div>
  );
}
